import { useId, useMemo, useState } from 'react'
import { groupFragrancesByName, queryHasConcentration, variantMatchesConcentrationFilter } from '../../lib/fragranceNameGrouping'
import { resolvedConcentrationDisplay } from '../../lib/concentrationResolver'
import { BottleThumbnail } from '../collection/HierarchyMultiAddModal'
import { ConcentrationPill } from '../add/CatalogResultVisuals'

const hasConcentrationLabel = value => value && value !== 'unknown' && value !== 'ambiguous'

/**
 * Name-grouped search rows: one row per fragrance name, expanding to its
 * concentration sub-rows. A name with a single bottle renders as one
 * selectable row. This is the list `CatalogSearchGrouped` renders inside
 * each house section; it carries no house or line logic of its own.
 *
 * @param {Array} rows - Search result rows (catalog rows)
 * @param {string} query - Current search query; a whole-word concentration
 *   term in it ("sauvage edp") narrows the sub-rows to that concentration
 * @param {Function} onSelect - Called with the chosen catalog row
 * @param {Function} [isOwned] - Ownership check for a row
 * @param {boolean} [disabled] - Whether selection is disabled
 * @param {boolean} [showHouse] - Print the house under each name (off inside house sections)
 * @param {Function} [renderAction] - Optional trailing action per bottle row, e.g. a wish-list button
 * @param {string|null} [emptyMessage] - Empty-state copy; null renders nothing when empty
 */
export default function GroupedSearchResults({
  rows,
  results,
  query = '',
  onSelect,
  isOwned = () => false,
  disabled = false,
  showHouse = true,
  renderAction,
  emptyMessage = 'No results found',
}) {
  const sourceRows = rows || results || []
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())
  const instance = useId()

  const groups = useMemo(() => groupFragrancesByName(sourceRows), [sourceRows])

  const hasConcentrationFilter = queryHasConcentration(query)
  const visibleGroups = useMemo(() => {
    const filtered = groups
      .map(group => ({
        ...group,
        visibleVariants: hasConcentrationFilter
          ? group.variants.filter(variant => variantMatchesConcentrationFilter(variant, query))
          : group.variants,
      }))
      .filter(group => group.visibleVariants.length > 0)
    // The search service already judged these rows relevant. If the
    // concentration filter would hide every one of them (unresolved
    // concentrations, say), show the rows rather than an empty list.
    if (!filtered.length && groups.length) {
      return groups.map(group => ({ ...group, visibleVariants: group.variants }))
    }
    return filtered
  }, [groups, hasConcentrationFilter, query])

  const toggleGroup = (groupKey) => {
    setExpandedGroups(previous => {
      const next = new Set(previous)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  const groupKey = (group) => `${group.house}\u0000${group.name}`
  const groupPanelId = (index) => `${instance}-grouped-panel-${index}`
  // A lone multi-concentration name opens on arrival: the only choice left is
  // which bottle, so it should not take a second tap.
  const onlyGroup = visibleGroups.length === 1

  return (
    <div className="divide-y divide-white/5" data-grouped-search-results>
      {visibleGroups.map((group, index) => {
        const key = groupKey(group)
        const { visibleVariants } = group

        if (visibleVariants.length === 1) {
          const variant = visibleVariants[0]
          const owned = isOwned(variant)
          return (
            <div key={key}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSelect(variant)}
                data-catalog-id={variant.id}
                data-in-collection={owned}
                data-fragrance-row="single"
                data-search-step="fragrance-single"
                className="flex min-h-16 w-full items-center gap-2 rounded-lg p-2 text-left hover:bg-gold/5 disabled:opacity-50"
              >
                <BottleThumbnail row={variant} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-cream">{group.name}</span>
                  {showHouse && <span className="block text-xs text-cream/50">{group.house}</span>}
                  <span className="block text-[11px] text-cream/40">{variant.year || 'Year unverified'}</span>
                  {owned && <span className="block text-[11px] font-semibold text-sage">In your collection</span>}
                </span>
                <ConcentrationPill concentration={resolvedConcentrationDisplay(variant)} />
              </button>
              {renderAction?.(variant, owned)}
            </div>
          )
        }

        const panelId = groupPanelId(index)
        const isExpanded = onlyGroup || expandedGroups.has(key)
        return (
          <div key={key} data-fragrance-group>
            <button
              type="button"
              disabled={disabled}
              onClick={() => { if (!onlyGroup) toggleGroup(key) }}
              className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left hover:bg-white/5 disabled:opacity-50"
              aria-expanded={isExpanded}
              aria-controls={panelId}
              data-fragrance-row="group"
              data-search-step="fragrance"
            >
              <BottleThumbnail row={visibleVariants[0]} size="line" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-cream">{group.name}</span>
                {showHouse && <span className="block text-xs text-cream/50">{group.house}</span>}
                <span className="block text-[11px] text-cream/40">
                  {visibleVariants.length} concentrations
                </span>
              </span>
              <span aria-hidden="true" className="shrink-0 text-gold">{isExpanded ? '−' : '+'}</span>
            </button>

            {isExpanded && (
              <div
                id={panelId}
                role="group"
                aria-label={`${group.name} concentrations`}
                className="space-y-1 px-2 pb-2"
                data-variant-list
              >
                {visibleVariants.map((variant) => {
                  const owned = isOwned(variant)
                  const concentration = resolvedConcentrationDisplay(variant)
                  return (
                    <div key={variant.id}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onSelect(variant)}
                        data-catalog-id={variant.id}
                        data-in-collection={owned}
                        data-fragrance-row="variant"
                        data-search-step="concentration"
                        data-concentration={concentration}
                        className="flex min-h-14 w-full items-center gap-2 rounded-lg border border-white/10 px-2 py-2 text-left hover:bg-gold/5 disabled:opacity-50"
                      >
                        <BottleThumbnail row={variant} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-cream">
                            {hasConcentrationLabel(concentration) ? concentration : variant.name}
                          </span>
                          <span className="block text-[11px] text-cream/40">{variant.year || 'Year unverified'}</span>
                          {owned && <span className="block text-[11px] font-semibold text-sage">In your collection</span>}
                        </span>
                        {!hasConcentrationLabel(concentration) && <ConcentrationPill concentration={concentration} />}
                      </button>
                      {renderAction?.(variant, owned)}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {visibleGroups.length === 0 && emptyMessage && (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-cream/45">{emptyMessage}</p>
        </div>
      )}
    </div>
  )
}
